import * as React from 'react';
import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';

import { useTheme } from '../../../../../theme';
import { OverrideComponent } from '../../../../../renderProps';
import { Sorting } from '../../../../../Results/enums';
import { getIsExchangeAutoMode } from '../../../../../Checkout/store/order/selectors';
import { sortFlights } from '../../../../../Results/components/Results/FlightsList/utils';
import Sortings from '../../../../../Results/components/Results/FlightsList/Sortings/Sortings';
import Disclaimer from '../../../../../Modules/Exchange/components/Exchange/Disclaimer/Disclaimer';
import ResultsError from '../../../../../ResultsError';

import { getFilteredExchangeVariantsOnLeg } from '@entities/exchange';
import { SelectFlight } from '@features/exchange/select-flight';

interface Props {
	className?: string;
	controls?: React.ReactNode;
}

const SearchFlights: React.FC<Props> = ({ className, controls }) => {
	const { t } = useTranslation('Exchange');
	const { SearchFlights: theme } = useTheme('Widgets').Exchange;

	const [sorting, setSorting] = useState(Sorting.Price);

	const isAutoMode = useSelector(getIsExchangeAutoMode);
	const filteredExchangeFlights = useSelector(getFilteredExchangeVariantsOnLeg);

	const sortedFlights = useMemo(() => {
		return sortFlights(filteredExchangeFlights, sorting);
	}, [filteredExchangeFlights, sorting]);

	const showSortControl = sortedFlights.length > 1;

	return (
		<>
			<div className={cn(theme.root, className)}>
				<div className={theme.content}>
					{(showSortControl || controls) && (
						<div className={theme.controls}>
							{showSortControl && <Sortings onSelect={setSorting} currentSort={sorting} />}
							{controls}
						</div>
					)}

					{sortedFlights.length > 0 && (
						<div className={theme.notifications}>
							{!isAutoMode && <Disclaimer type="price" />}
							<Disclaimer type="time" />
						</div>
					)}
				</div>

				{sortedFlights.length ? (
					<div className={theme.flights}>
						{sortedFlights.map((flight, index) => (
							<SelectFlight key={index} flight={flight} />
						))}
					</div>
				) : (
					<ResultsError
						header={t(`We couldn't find any flights for given dates`)}
						body={
							<OverrideComponent
								componentProps={{}}
								component={{
									ExchangeErrorText: () => (
										<div
											dangerouslySetInnerHTML={{
												__html: t(
													'Please select another date or modify your search. If you wish to purchase a ticket for this particular date, please contact customer support.'
												)
											}}
										/>
									)
								}}
							/>
						}
					/>
				)}
			</div>
		</>
	);
};

export default SearchFlights;
